import Config from 'services/config/config';
import callWebService, { callTypes } from 'services/utils/networks/network';
import { REFERRAL_FLOW } from 'pages/valetServices/siteLeadReferral/constants';
import { parseSpocListResponse } from 'pages/valetServices/siteLeadReferral/referralUtils';
import {
  SITE_LEAD_REFERRAL_LIST,
  SITE_LEAD_REFERRAL_SPOC_LIST,
  SITE_LEAD_REFERRAL_REJECT_REASONS,
  SITE_LEAD_REFERRAL_STATUS_HISTORY,
} from './type';

const emptySpocOptions = { regionOptions: [], spocOptionsByRegion: {} };

const emptyList = {
  referrals: [],
  count: 0,
  page: 1,
  limit: 20,
  incentiveAmounts: null,
};

const buildListQuery = (page = 1, filters = {}) => {
  const params = new URLSearchParams();
  params.set('referral_flow', filters.referral_flow || REFERRAL_FLOW);
  params.set('page', String(page));
  params.set('limit', String(filters.limit || 20));
  if (filters.status) {
    params.set('status', String(filters.status));
  }
  if (filters.include_legacy === true || filters.include_legacy === 'true') {
    params.set('include_legacy', 'true');
  } else {
    params.set('include_legacy', 'false');
  }
  return params.toString();
};

const parseListResponse = (res, page, filters) => {
  if (res?.data?.status === 0) {
    return {
      referrals: res.data.data || [],
      count: res.data.count ?? 0,
      page: res.data.page ?? page,
      limit: res.data.limit ?? filters.limit ?? 20,
      incentiveAmounts: res.data.incentive_amounts || null,
    };
  }
  return null;
};

export const getReferralList = (page = 1, filters = {}) => async (dispatch) => {
  const url = `${Config.siteLeadReferral.list}?${buildListQuery(page, filters)}`;
  const res = await callWebService(callTypes.get, '', url);
  const payload = parseListResponse(res, page, filters);

  if (payload) {
    dispatch({ type: SITE_LEAD_REFERRAL_LIST, payload });
    return payload;
  }

  const message = res?.data?.message || 'Failed to load referrals';
  dispatch({ type: SITE_LEAD_REFERRAL_LIST, payload: emptyList });
  throw new Error(message);
};

export const getSpocList = () => async (dispatch) => {
  const res = await callWebService(callTypes.get, '', Config.siteLeadReferral.listSpoc);

  if (res?.data?.status === 0) {
    const payload = parseSpocListResponse(res.data.data || []);
    dispatch({ type: SITE_LEAD_REFERRAL_SPOC_LIST, payload });
    return payload;
  }

  const message = res?.data?.message || 'Failed to load SPOCs';
  dispatch({ type: SITE_LEAD_REFERRAL_SPOC_LIST, payload: emptySpocOptions });
  throw new Error(message);
};

export const getRejectReasons = () => async (dispatch) => {
  const res = await callWebService(callTypes.get, '', Config.siteLeadReferral.rejectReasons);

  if (res?.data?.status === 0) {
    const reasons = res.data.data?.reasons || [];
    dispatch({ type: SITE_LEAD_REFERRAL_REJECT_REASONS, payload: reasons });
    return reasons;
  }

  const message = res?.data?.message || 'Failed to load reject reasons';
  dispatch({ type: SITE_LEAD_REFERRAL_REJECT_REASONS, payload: [] });
  throw new Error(message);
};

export const getStatusHistory = (requestId) => async (dispatch) => {
  dispatch({ type: SITE_LEAD_REFERRAL_STATUS_HISTORY, payload: [] });

  const res = await callWebService(
    callTypes.get,
    '',
    Config.siteLeadReferral.statusHistory(requestId)
  );

  if (res?.data?.status === 0) {
    const history = res.data.data || [];
    dispatch({ type: SITE_LEAD_REFERRAL_STATUS_HISTORY, payload: history });
    return history;
  }

  const message = res?.data?.message || 'Failed to load status history';
  throw new Error(message);
};

const patchReferralWorkflow = async (url, payload) => {
  const res = await callWebService(callTypes.patch, payload, url);

  if (res?.data?.status === 0) {
    return res.data;
  }

  const message = res?.data?.message || 'Workflow action failed';
  throw new Error(message);
};

export const assignSpoc = (requestId, payload) => () =>
  patchReferralWorkflow(Config.siteLeadReferral.assignSpoc(requestId), payload);

export const validateLead = (requestId, payload) => () =>
  patchReferralWorkflow(Config.siteLeadReferral.validate(requestId), payload);

export const rejectLead = (requestId, payload) => () =>
  patchReferralWorkflow(Config.siteLeadReferral.reject(requestId), payload);

export const centralValidate = (requestId, payload) => () =>
  patchReferralWorkflow(Config.siteLeadReferral.centralValidate(requestId), payload);

export const setLeadStage = (requestId, payload) => () =>
  patchReferralWorkflow(Config.siteLeadReferral.stage(requestId), payload);
